/*
 * SPEAKER SCRIPT — the master deck (`v2026.10.05`).
 *
 * ⚠️ THIS FILE WAS `speakerNotes.ts`, AND THE CONCEPT WAS CALLED "SPEAKER NOTES" UNTIL 2026-10-12
 * (`@P280` `T0316` · `D-280-032` · `RULES_NAMING` §4/§5). The PO retired the name: *"`Speaker Notes`
 * is ambiguous! 3 min script IS speaker notes."* — a name that describes the slide it sits beside
 * rather than the thing itself, which is how a SCRIPT and a set of bullet points end up sharing one
 * word. The new name says what the thing IS: the script she speaks from.
 *
 * *Deliberately NOT renamed, and the boundary is a decision rather than an oversight:* the deck
 * model's `notes` FIELD (`PitchDeckV2Page`) — the read-along strip's own data, an identifier on
 * `@P109`'s surface rather than this concept's name. Only the words a READER sees are canonical
 * (`RULES_NAMING` §4), and those moved with the file.
 *
 * MIRROR, NOT A HOME (`DRAISY` MR0pv, "a second home for one fact is a defect").
 * The source of truth is the `**NOTES:**` line under each slide in
 *   playano-management/raise/decks/Raisy — Pitch Deck v2026.10.05.md
 * which is the deck's approval artefact (`D-075`). This file exists only because the front end
 * cannot import markdown from the management repo. Same arrangement as `marketByYear.ts`
 * mirroring FM-0013: edit the source, then mirror.
 *
 * LENGTH IS A BUDGET, AND THE BUDGET WAS RAISED (PO 2026-10-09).
 * `D-103` set ~15 words a note against a 120-second pitch. He then reviewed the deck against the
 * co-founder's: *"compared to simon speaker notes length. i think we are too short here!.. we have
 * 15 slides. 3 minutes time. so a bit longer please."*
 *
 * THE ARITHMETIC IS HIS, AND IT IS NOW THE BUDGET: 3 minutes = 180 seconds over 15 spoken slides
 * = **12 seconds a slide**, which at a normal speaking pace is **~27 words**. Every note below
 * lands between 23 and 31. The test is unchanged — a note must say something the headline does
 * NOT, or it spends its twelve seconds repeating what the reader can already see. Written to be
 * SPOKEN: contractions, no semicolons, no brackets, no number a listener cannot hold.
 *
 * ⚠️ NOTE ORDER FOLLOWS SLIDE ORDER, AND THE SLIDE ORDER CHANGED on 2026-10-09: the deck now opens
 * on Maya (the problem) and the hook moved to position 3 (the promise). Notes 2 and 3 swapped with
 * them — this array is indexed by POSITION, so a swap that does not move the notes puts the wrong
 * script under both slides.
 *
 * Rendered as a floating pill in the topbar band, NOT a strip that reserves height (`D-103`) —
 * reserving height shrank `.pd-slide` and scaled every frozen layout down.
 */
export const SCRIPT_MASTER: string[] = [
  // 1 · Cover
  "We're raising five hundred thousand pre-seed, and we're running our own raise on Raisy to prove it works. First close is the fifteenth of November.",
  // 2 · Meet Maya — the problem, and the deck now OPENS here
  "This is Maya. Pre-seed founder, first round, nobody behind her. She spent forty-seven hours building one list over three weeks, and eighty-eight percent was wrong-fit. That's the problem.",
  // 3 · The hook — the shift, now the promise
  "So the machine takes the research, the lists, the drafts and the follow-ups. What's left is what only she can do: deciding, approving, and being in the room.",
  // 4 · What we ship today
  "Live since October. She pastes her URL, we build the entity, score three and a half thousand investors and draft every message. Nothing leaves without her approval.",
  // 5 · The proof it already works
  "The one-person company is already the best structure we can point at. Medvi did four hundred million in year one with two people. The memory layer behind it is fundable.",
  // 6 · The honest traction
  "Here's exactly what exists and what doesn't. Version one shipped, PopMeals is our design partner, four founders on the waitlist. No paying customers yet, and we say so.",
  // 7 · The category
  "Fundraising is the door, not the ceiling. Every B2B process runs the same four steps — connect, communicate, meet, sign. Same engine, different counterpart, one memory.",
  // 8 · Why we are different
  "Everyone else sells a model. We sell the management layer: entity, process, bot, kernel. Every level carries KPIs, so no number depends on a prompt.",
  // 9 · The value
  "One adopted customer is worth six thousand three hundred a year against a six hundred dollar subscription. Ten point five times. The model computes that, we didn't assert it.",
  // 10 · The market
  "This is bottom-up, not a slice of a big number. Forty thousand founders are raising this year, and our market is that population times what one spends.",
  // 11 · The model
  "Free to start, then forty-nine, a hundred and twenty-nine, two seventy-nine a month, plus one percent when the outcome lands. The transaction that closed, not the email that hoped.",
  // 12 · GTM
  "One service a quarter, on customers we already have. Fundraising brings her in, every other lane attaches to her. The months come straight from the model.",
  // 13 · The team
  "Two founders who've shipped and exited nine products, and we're building this for ourselves first. Legal, creative, technical and product advisors behind us.",
  // 14 · The round — ask
  "Five hundred thousand on a SAFE, seven and a half pre-money, fifty thousand minimum. One ticket returns about three forty-seven at the modelled exit. First close November fifteenth.",
  // 15 · The vision — close
  "So here's what we're testing. Can one system that never forgets run a company? Fundraising is the first proof — then sales, hiring and procurement on the same memory.",
  // 16 · Legal
  "Not investment advice, and not an offer. If you want any number walked through, or the model itself, ask me and I'll send it today.",
];
